import type { Dict } from '@chakra-ui/utils';
import { ComponentState } from 'ts/common/constants/theme/common';
import { colors } from 'ts/studio/colors';
import fonts from 'ts/studio/fonts';
import { accordionTheme } from 'ts/studio/themes/accordionTheme';
import { alertTheme } from 'ts/studio/themes/alertTheme';
import { buttonTheme } from 'ts/studio/themes/buttonTheme';
import { inputTheme } from 'ts/studio/themes/inputTheme';
import { radioTheme } from 'ts/studio/themes/radioTheme';
import { stepperTheme } from 'ts/studio/themes/stepperTheme';
import { switchTheme } from 'ts/studio/themes/switchTheme';
import { tableTheme } from 'ts/studio/themes/tableTheme';
import { tagTheme } from 'ts/studio/themes/tagTheme';

type ResponsiveStyleParam = TemplateStringsArray | string;

export function convertHexToRgba(hex: string, alpha = 1): string {
    const [, r1, r2, g1, g2, b1, b2] = Array.from(hex);
    const blue = parseInt(`${b1}${b2}`, 16);
    const green = parseInt(`${g1}${g2}`, 16);
    const red = parseInt(`${r1}${r2}`, 16);

    if (alpha > 1) {
        alpha = 1;
    } else if (alpha < 0) {
        alpha = 0;
    }

    return `rgba(${red},${green},${blue},${alpha})`;
}

/**
 * @deprecated Use colors.primary[300] instead
 */
export const navy300 = '#1280BE';

/**
 * @deprecated Use colors.primary[400] instead
 */
export const navy600 = '#0C547D'; // This is the "primary" color used for Primary Buttons, for example.

/**
 * @deprecated Use colors.primary[600] instead
 */
export const navy700 = '#002B4C';

/**
 * @deprecated Use colors.neutral[300] instead
 */
export const gray300 = '#BCC8CD';

/**
 * @deprecated Use colors.secondary[400] instead
 */
export const blue400 = '#49BDE4';

const breakpoints = {
    base: '0',
    sm: '480',
    md: '768',
    lg: '1200',
    xl: '1900',
    '2xl': '2400'
};

const { md, lg, xl } = breakpoints;
const responsive = {
    extraLargeScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(xl) + 1}px) {
      ${rules}
  }
`,
    largeScreenDown: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${xl}px) {
      ${rules}
  }
`,
    largeScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(lg) + 1}px) and (max-width: ${xl}px) {
      ${rules}
  }
`,
    largeScreenUp: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(lg) + 1}px) {
      ${rules}
  }
`,
    mediumScreenDown: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${lg}px) {
      ${rules}
  }
`,
    mediumScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(md) + 1}px) and (max-width: ${lg}px) {
      ${rules}
  }
`,
    mediumScreenUp: (rules: ResponsiveStyleParam) => `
  @media only screen and (min-width: ${Number(md) + 1}px) {
      ${rules}
  }
`,
    smallScreenOnly: (rules: ResponsiveStyleParam) => `
  @media only screen and (max-width: ${md}px) {
      ${rules}
  }
`
};

const components = {
    Accordion: accordionTheme,
    Alert: alertTheme,
    Button: buttonTheme,
    Checkbox: {
        baseStyle: {
            control: {
                backgroundColor: 'white'
            }
        },
        customStyle: {
            backgroundColor: colors.gray[100],
            borderColor: colors.gray[400],
            checkColor: colors.blue[400],
            checkWidth: '8px',
            width: '16px',
            [ComponentState.Disabled]: {
                backgroundColor: colors.gray[100],
                borderColor: colors.gray[200],
                checkColor: colors.blue[100]
            }
        }
    },
    DatePicker: {
        baseStyle: {
            fontFamily: fonts.heading,
            // Selected date background color (primary blue - matches Figma)
            backgroundColor: colors.primary[400], // #0C547D
            // Selected date text color
            dateColor: 'white',
            // Calendar popup header background
            headerBackgroundColor: 'white',
            // Calendar background (main calendar area)
            calendarBackgroundColor: 'white',
            // Popper background (popup container)
            popperBackgroundColor: 'white',
            // Day text color (matches Figma Neutral/600)
            color: colors.neutral[600], // #344146
            // Disabled day text color
            disabledColor: colors.neutral[300], // #BCC8CD
            // Day header color (S M T W T F S)
            dayHeaderColor: '#141617',
            // Popup border color
            borderColor: colors.gray[200], // #D9E0E3
            // Popup border radius
            borderRadius: '8px',
            // Popup box shadow
            boxShadow: '0 4px 16px rgba(159, 176, 183, 0.3)',
            // Calendar padding
            padding: '16px',
            // Day cell size
            daySize: '36px',
            // Day name width
            dayNameWidth: '36px',
            // Day font size
            dayFontSize: '14px',
            // Hover background color
            hoverBackgroundColor: '#EDF1F3',
            // Hover border radius
            hoverBorderRadius: '33px',
            // Week gap (tighter for studio)
            weekGap: '4px',
            // Week padding (tighter for studio)
            weekPadding: '2px 0',
            // Day names gap (tighter for studio)
            dayNamesGap: '4px',
            // Header styling (matches Figma)
            headerBorderColor: colors.gray[200], // #D9E0E3
            headerPaddingBottom: '12px',
            chevronSize: '24px',
            chevronColor: colors.neutral[600], // #344146 - matches month/year text
            monthYearFontSize: 'inherit',
            monthYearFontWeight: 'normal',
            // Today button styling
            todayButtonColor: colors.secondary[400]
        }
    },
    Form: {
        baseStyle: {
            requiredIndicator: {
                color: 'inherit',
                margin: 0
            }
        }
    },
    FormErrorMessage: {
        baseStyle: {
            color: colors.red[300],
            fontWeight: 300
        }
    },
    FormHelperText: {
        baseStyle: {
            fontWeight: 300
        }
    },
    FormLabel: {
        baseStyle: {
            fontSize: 16,
            lineHeight: '25px',
            color: 'neutral.600'
        },
        sizes: {
            lg: {
                fontSize: 16
            }
        }
    },
    Input: inputTheme,
    Switch: switchTheme,
    Menu: {
        variants: {
            contextMenu: {
                button: {
                    height: '24px',
                    padding: 0,
                    right: 0,
                    top: 0,
                    width: '24px'
                },
                groupTitle: {
                    color: 'neutral.600',
                    marginLeft: '0px',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    padding: '10px 8px',
                    marginTop: '0px',
                    marginBottom: '0px'
                },
                item: {
                    borderRadius: '8px',
                    color: 'primary.400',
                    display: 'flex',
                    fontSize: '14px',
                    fontWeight: 600,
                    lineHeight: '14px',
                    padding: '10px 12px'
                },
                divider: {
                    display: 'none'
                },
                list: {
                    borderRadius: '12px',
                    minW: 'unset',
                    padding: '10px',
                    width: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                }
            }
        }
    },
    Modal: {
        baseStyle: {
            header: {
                color: 'neutral.600'
            }
        }
    },
    Select: {
        baseStyle: {
            field: {
                fontSize: '16px',
                color: 'neutral.600'
            }
        }
    },
    Stepper: stepperTheme,
    Radio: radioTheme,
    Tabs: {
        variants: {
            line: {
                tab: {
                    color: 'neutral.600',
                    _selected: { color: 'neutral.600', fontWeight: 600, borderColor: 'primary.400' }
                }
            }
        }
    },
    Text: {
        baseStyle: {
            fontFamily: fonts.heading
        },
        variants: {
            error: {
                color: 'red.400'
            }
        }
    },
    Textarea: {
        baseStyle: {
            fontSize: '16px'
        }
    },
    Tooltip: {
        baseStyle: {
            background: 'primary.600',
            borderRadius: '8px',
            color: 'white',
            fontSize: '12px',
            lineHeight: '12px',
            fontWeight: 300,
            padding: '6px'
        },
        variants: {
            bold: {
                textTransform: 'uppercase',
                fontWeight: 500,
                padding: '8px',
                letterSpacing: '1.4px'
            },
            light: {
                '--popper-arrow-bg': 'white',
                background: 'white',
                color: 'gray.700'
            }
        }
    },
    Table: tableTheme,
    Tag: tagTheme
};

const getTheme = (): Dict => {
    return {
        colors,
        fonts,
        fontColor: {
            default: 'inherit'
        },
        styles: {
            global: {
                body: {
                    color: 'gray.600',
                    fontFamily: null,
                    lineHeight: null,
                    transition: null
                },
                input: {
                    fontFamily: fonts.body,
                    color: 'gray.600'
                },
                select: {
                    fontFamily: fonts.body,
                    color: 'gray.600'
                }
            }
        },
        components,
        responsive
    };
};

export default getTheme;
